import { Link } from 'react-router-dom';
import { pageMeta } from '../../constants/pageMeta';
import { COMPANY_NAME, SITE_DOMAIN, SITE_NAME } from '../../constants/site';
import PolicyPage, { ContactBlock, List, Section } from '../components/PolicyPage';

const META = pageMeta(
  'Terms & Conditions',
  `The terms that apply when you use ${SITE_DOMAIN} or book travel with the help of ${SITE_NAME} (${COMPANY_NAME}).`,
  '/terms-and-conditions'
);

export default function TermsPage() {
  return (
    <PolicyPage
      title="Terms & Conditions"
      meta={META}
      intro={`These terms apply when you use ${SITE_DOMAIN} or book travel through ${SITE_NAME}, a trade name of ${COMPANY_NAME}. Please read them before you book.`}
    >
      <Section title="1. About us and our role">
        <p>
          {COMPANY_NAME} (“we”, “us”) is an independent travel agency. We are not an airline, hotel, car rental
          company, cruise line or tour operator, and we are not affiliated with or endorsed by any travel provider.
          When we book travel for you, we act as your agent: your contract for the travel itself is with the travel
          provider, and the provider’s own conditions of carriage or terms of service apply to it.
        </p>
      </Section>

      <Section title="2. Quotes">
        <List
          items={[
            'Quotes are free and do not commit you to anything.',
            'Each quote lists the provider price, taxes and charges, and our service fee separately.',
            'Prices are set by travel providers and are not guaranteed until the booking is confirmed. If a price changes before booking, we will tell you and ask again before going ahead.',
            'Any price shown on this website is an example only. We do not display live fares on this website.',
          ]}
        />
      </Section>

      <Section title="3. Making a booking">
        <List
          items={[
            'We only book after you clearly approve the final total, by phone (recorded where you are told so), email or a signed authorisation.',
            'You must give names exactly as they appear on each traveller’s passport or government ID and check every detail in your confirmation straight away.',
            'You must be at least 18 and have authority to book for everyone in your party.',
            'Your booking is confirmed when you receive a confirmation from us that includes the provider’s booking reference.',
          ]}
        />
      </Section>

      <Section title="4. Payment">
        <p>
          Payment may be taken by us or directly by the travel provider; your quote tells you which. Some charges may
          appear on your statement as separate transactions from the provider and from {COMPANY_NAME}. Our service fee
          is described on the <Link to="/service-fees">Service Fees</Link> page.
        </p>
      </Section>

      <Section title="5. Changes, cancellations and refunds">
        <p>
          Changes and cancellations follow the travel provider’s rules and may involve provider penalties, fare
          differences and our change fee. See our{' '}
          <Link to="/cancellation-refund-policy">Cancellation &amp; Refund Policy</Link> for full details.
        </p>
      </Section>

      <Section title="6. Your responsibilities">
        <List
          items={[
            'Holding valid passports, visas, health documents and any travel authorisations required for your trip.',
            'Checking in on time and meeting the provider’s baggage, conduct and safety rules.',
            'Reading the provider rules and the confirmation we send you.',
            'Telling us promptly about any error you notice.',
          ]}
        />
      </Section>

      <Section title="7. Schedule changes and provider failures">
        <p>
          Travel providers may change schedules, cancel services or change terms. We will pass on information we
          receive and help you with your options, but we are not responsible for a provider’s acts or omissions,
          including delays, cancellations, overbooking or insolvency.
        </p>
      </Section>

      <Section title="8. Travel insurance">
        <p>We strongly recommend suitable travel insurance. If we help you buy insurance, the insurer’s policy terms apply.</p>
      </Section>

      <Section title="9. Limitation of liability">
        <p>
          To the extent permitted by law, our liability to you for any claim related to our services is limited to the
          service fees you paid us for the booking concerned. We are not liable for indirect or consequential losses.
          Nothing in these terms limits liability that cannot be limited by law.
        </p>
      </Section>

      <Section title="10. Using this website">
        <p>
          The content of this website is provided for general information. You may not copy it for commercial use,
          misuse the website or try to access it in an unauthorised way. Links to other websites are provided for
          convenience; we are not responsible for their content.
        </p>
      </Section>

      <Section title="11. Complaints">
        <p>
          Please contact us first using the details below so we can try to put things right. See our{' '}
          <Link to="/contact-us">Contact Us</Link> page for how we handle complaints.
        </p>
      </Section>

      <Section title="12. Governing law">
        <p>
          These terms are governed by the laws of the State of Wyoming, United States, without affecting any consumer
          protection rights you have under the laws of the place where you live.
        </p>
      </Section>

      <Section title="13. Contact us">
        <ContactBlock />
      </Section>
    </PolicyPage>
  );
}
